import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { 
  Card, Row, Col, Statistic, Progress, Tag, 
   Divider, Empty, 
  Table, Tabs
} from 'antd';
import { 
  BarChartOutlined, 
   ApartmentOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../../../../api';

const { TabPane } = Tabs;

// --- Faculty name fetch helper ---
const fetchFacultyNameById = async (userId, token) => {
  if (!userId) return '';
  try {
    const res = await api.get(`/api/users/${userId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });
    const user = res.data;
    return user.name || user.firstName || user.fullName || '';
  } catch {
    return '';
  }
};

const AnalyticsDashboard = ({ 
  weekTemplate = [], 
  subjects = [], 
  facultyData = [],
  electiveGroups = [],
  languageGroups = [],
  facultyNameMap = {}
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [fetchedFacultyNames, setFetchedFacultyNames] = useState({});

  // 1. HELPERS
  const formatNumber = (num) => (num === null || isNaN(num) ? '0' : Math.round(num).toString());
  
  const calculateDuration = (start, end) => {
    try {
      const s = moment(start, 'HH:mm');
      const e = moment(end, 'HH:mm');
      if (e.isBefore(s)) e.add(1, 'day');
      return e.diff(s, 'hours', true);
    } catch { return 0; }
  };

  // 2. AGGRESSIVE DATA RESOLUTION
  const { facultyMap, subjectMap, groupDetails } = useMemo(() => {
    const fMap = new Map(); // Faculty
    const sMap = new Map(); // Subjects
    const gMap = new Map(); // Detailed Group Data

    // --- Helper to register faculty ---
    const registerFaculty = (id, name) => {
      const strId = String(id || '');
      if (!strId) return;
      const current = fMap.get(strId);
      const isNewNameGeneric = !name || name === 'Faculty Member' || name === 'Not Assigned';
      if (!current || (current.isGeneric && !isNewNameGeneric)) {
        fMap.set(strId, {
          id: strId,
          name: isNewNameGeneric ? (facultyNameMap[strId] || 'Faculty Member') : name,
          isGeneric: isNewNameGeneric
        });
      }
    };

    // Helper to register subjects
    const registerSubject = (id, name, code) => {
      const strId = String(id || '');
      if (!strId) return;
      if (!sMap.has(strId)) {
        sMap.set(strId, { name: name || 'Unknown Subject', code: code || '' });
      }
    };

    // A. Load Static Data (Low Priority)
    facultyData.forEach(f => registerFaculty(f._id || f.id || f.userId, f.name || f.fullName));
    subjects.forEach(s => registerSubject(s._id || s.id, s.name, s.code));

    // B. Deep Scan Timetable (High Priority for specific assignments)
    weekTemplate.forEach(entry => {
      // 1. Faculty from main entry
      if (entry.facultyUserId) registerFaculty(entry.facultyUserId, entry.facultyName);
      
      // 2. Groups & Electives
      if (entry.electiveSelections?.length > 0) {
        const groupId = String(entry.electiveGroupId || 'Unk');
        if (!gMap.has(groupId)) {
          gMap.set(groupId, {
            id: groupId,
            name: entry.electiveGroupName || 'Elective Group',
            items: []
          });
        }
        const groupEntry = gMap.get(groupId);

        entry.electiveSelections.forEach(sel => {
          registerSubject(sel.subjectId, sel.subjectName);

          // Subgroups
          if (sel.subgroupSelections?.length > 0) {
            sel.subgroupSelections.forEach(sub => {
              registerFaculty(sub.facultyId, sub.facultyName);
              groupEntry.items.push({
                subjectId: sel.subjectId,
                subjectName: sel.subjectName || sMap.get(sel.subjectId)?.name,
                subgroupName: sub.subGroupName || `Subgroup ${sub.subGroupId}`,
                facultyId: sub.facultyId,
                facultyName: sub.facultyName || fMap.get(sub.facultyId)?.name || 'Faculty Member'
              });
            });
          } 
          // No Subgroups (Direct Elective)
          else if (sel.facultyId) {
            registerFaculty(sel.facultyId, sel.facultyName);
            groupEntry.items.push({
              subjectId: sel.subjectId,
              subjectName: sel.subjectName || sMap.get(sel.subjectId)?.name,
              subgroupName: 'Main',
              facultyId: sel.facultyId,
              facultyName: sel.facultyName || fMap.get(sel.facultyId)?.name
            });
          }
        });
      }
    });

    return { facultyMap: fMap, subjectMap: sMap, groupDetails: Array.from(gMap.values()) };
  }, [weekTemplate, facultyData, subjects, facultyNameMap]);

  // Wrap in useCallback for stable reference
  const getFacultyName = useCallback(
    (id) => facultyMap.get(String(id))?.name || 'Faculty Member',
    [facultyMap]
  );
  const getSubjectName = useCallback(
    (id) => subjectMap.get(String(id))?.name || 'Subject',
    [subjectMap]
  );

  // 3. Fetch missing faculty names for groupDetails
  useEffect(() => {
    // Collect all faculty IDs from groupDetails
    const idsToFetch = new Set();
    groupDetails.forEach(group => {
      group.items.forEach(item => {
        if (
          item.facultyId &&
          (!facultyMap.get(String(item.facultyId)) ||
            facultyMap.get(String(item.facultyId)).name === 'Faculty Member')
        ) {
          idsToFetch.add(item.facultyId);
        }
      });
    });
    if (idsToFetch.size === 0) return;

    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || null;
    const fetchAll = async () => {
      const names = {};
      for (const id of idsToFetch) {
        names[id] = await fetchFacultyNameById(id, token);
      }
      setFetchedFacultyNames(prev => ({ ...prev, ...names }));
    };
    fetchAll();
  }, [groupDetails, facultyMap]);

  // 4. CORE STATS CALCULATION
  const analytics = useMemo(() => {
    if (!weekTemplate.length) return null;

    const stats = {
      totalHours: 0,
      totalSessions: 0,
      facultyStats: new Map(),
      groupStats: new Map(),
      subjectStats: new Map(),
      days: {},
      conflicts: []
    };

    weekTemplate.forEach(entry => {
      if (!entry.timeSlot?.start || !entry.timeSlot?.end) return;
      const duration = calculateDuration(entry.timeSlot.start, entry.timeSlot.end);
      
      if (entry.type === 'Break') return;

      stats.totalHours += duration;
      stats.totalSessions++;

      // -- Participants Collection --
      const participants = [];

      if (entry.electiveSelections?.length > 0) {
        const gId = String(entry.electiveGroupId || 'Unk');
        if (!stats.groupStats.has(gId)) {
          stats.groupStats.set(gId, { 
            id: gId, 
            name: entry.electiveGroupName || 'Group', 
            hours: 0, 
            facultyIds: new Set() 
          });
        }
        const gStat = stats.groupStats.get(gId);
        gStat.hours += duration;

        entry.electiveSelections.forEach(sel => {
          if (sel.subgroupSelections?.length > 0) {
            sel.subgroupSelections.forEach(sub => {
              if (sub.facultyId) {
                participants.push({ fid: sub.facultyId, sid: sel.subjectId, isGroup: true });
                gStat.facultyIds.add(sub.facultyId);
              }
            });
          } else if (sel.facultyId) {
            participants.push({ fid: sel.facultyId, sid: sel.subjectId, isGroup: true });
            gStat.facultyIds.add(sel.facultyId);
          }
        });
      } else {
        if (entry.facultyUserId) participants.push({ fid: entry.facultyUserId, sid: entry.subjectId, isGroup: false });
      }

      // -- Update Faculty & Subject Stats --
      const processedFaculty = new Set();
      participants.forEach(p => {
        if (processedFaculty.has(p.fid)) return;
        processedFaculty.add(p.fid);

        if (!stats.facultyStats.has(p.fid)) {
          stats.facultyStats.set(p.fid, { 
            id: p.fid, 
            name: getFacultyName(p.fid), 
            hours: 0, 
            subjects: new Set(),
            isGroup: false
          });
        }
        const f = stats.facultyStats.get(p.fid);
        f.hours += duration;
        f.subjects.add(p.sid);
        if (p.isGroup) f.isGroup = true;

        if (p.sid) {
           if (!stats.subjectStats.has(p.sid)) {
             stats.subjectStats.set(p.sid, { name: getSubjectName(p.sid), hours: 0 });
           }
           stats.subjectStats.get(p.sid).hours += duration;
        }
      });
    });

    const facultyList = Array.from(stats.facultyStats.values())
      .map(f => ({
        ...f,
        subjectCount: f.subjects.size,
        status: f.hours > 18 ? 'High' : f.hours < 8 ? 'Low' : 'Optimal',
        utilization: (f.hours / 24) * 100
      }))
      .sort((a,b) => b.hours - a.hours);

    return {
      metrics: {
        hours: stats.totalHours,
        sessions: stats.totalSessions,
        faculty: facultyList.length,
        groups: stats.groupStats.size
      },
      faculty: facultyList,
      groups: Array.from(stats.groupStats.values()),
      subjects: Array.from(stats.subjectStats.values()).sort((a,b) => b.hours - a.hours)
    };
  }, [weekTemplate, getFacultyName, getSubjectName]);

  // 5. RENDERERS

const renderGroupsDetail = () => {
  if (!groupDetails || groupDetails.length === 0) return <Empty description="No Group Data" />;

  // Group items by subject
  return (
    <div className="space-y-6">
      {groupDetails.map(group => {
        // Group items by subjectId
        const subjectMap = {};
        group.items.forEach(item => {
          if (!subjectMap[item.subjectId]) subjectMap[item.subjectId] = [];
          subjectMap[item.subjectId].push(item);
        });

        return (
          <Card key={group.id} title={<><ApartmentOutlined /> {group.name}</>} className="shadow-sm">
            <div className="space-y-4">
              {Object.entries(subjectMap).map(([subjectId, items]) => {
                const subjectName = items[0].subjectName;
                // Collect all unique faculty for this subject
                const facultyList = items
                  .map(r => {
                    const realName = getFacultyName(r.facultyId);
                    if (realName && realName !== 'Faculty Member') return realName;
                    if (fetchedFacultyNames[r.facultyId]) return fetchedFacultyNames[r.facultyId];
                    return null;
                  })
                  .filter((v, i, arr) => v && arr.indexOf(v) === i); // unique

                return (
                  <div key={subjectId} className="p-2 bg-gray-50 rounded">
                    <div className="font-semibold text-gray-800">{subjectName}</div>
                    <div className="mt-1 space-y-1">
                      {facultyList.length > 0 ? (
                        facultyList.map((name, idx) => (
                          <div key={idx} className="text-gray-700">
                            Faculty: {name}
                          </div>
                        ))
                      ) : (
                        <div className="text-red-400">Faculty: Not Assigned</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

  const renderOverview = () => {
    if (!analytics) return null;

    return (
      <Row gutter={16}>
        <Col span={8}>
          <Card>
            <Statistic title="Total Hours" value={formatNumber(analytics.metrics.hours)} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Total Sessions" value={formatNumber(analytics.metrics.sessions)} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Total Faculty" value={formatNumber(analytics.metrics.faculty)} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Total Groups" value={formatNumber(analytics.metrics.groups)} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Avg. Hours/Faculty" value={formatNumber(analytics.metrics.hours / analytics.metrics.faculty)} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Avg. Sessions/Faculty" value={formatNumber(analytics.metrics.sessions / analytics.metrics.faculty)} />
          </Card>
        </Col>
      </Row>
    );
  };

  // 6. FACULTY TABLE (Overview tab)
  const renderFacultyTable = () => (
    <Table 
      dataSource={analytics?.faculty || []}
      rowKey="id"
      columns={[
        { 
          title: 'Name', 
          dataIndex: 'name', 
          render: (t, r) => {
            const isGeneric = !t || t === 'Faculty Member';
            const resolved = !isGeneric ? t : (fetchedFacultyNames[r.id] || 'Not Assigned');
            return (
              <div>
                <div className="font-medium">{resolved}</div>
                {r.isGroup && <Tag className="mt-1" color="purple">Elective/Group</Tag>}
              </div>
            );
          }
        },
        { title: 'Total Hours', dataIndex: 'hours', sorter: (a,b) => a.hours - b.hours, render: h => formatNumber(h) },
        { title: 'Status', dataIndex: 'status', 
          render: s => <Tag color={s==='High'?'red':s==='Low'?'orange':'green'}>{s}</Tag> 
        },
        { title: 'Subjects', dataIndex: 'subjectCount' },
        { title: 'Load', dataIndex: 'utilization', render: u => <Progress percent={formatNumber(u)} size="small" /> }
      ]}
    />
  );

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<span><BarChartOutlined /> Overview</span>} key="overview">
          {renderOverview()}
          <Divider />
          {renderFacultyTable()}
        </TabPane>
        <TabPane tab={<span><ApartmentOutlined /> Elective & Language Groups</span>} key="groups">
          {renderGroupsDetail()}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;